/* ### Documentation ###
 *   Old: https://confluence.check24.de/display/HWF/Loader+Script#LoaderScript-IncludeChattyviaanyotherelement
 *   New: https://confluence.check24.de/pages/viewpage.action?pageId=1514184844
 */

import type {
    ChattyConfigType,
    ChattyDeviceoutputType,
    ChattySheetStateType,
} from '@/core/features/wireframe/wireframe-types';
import type { ChattyDataV1Attributes } from '@/features/chatty-data-v1/chatty-data-v1-type';

import React from 'react';

import { IS_PROD } from '@/constants/env';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import logger from '@/core/features/logger/logger';
import { WIREFRAME_DID_CHANGE_CHATTY_STATE_EVENT } from '@/core/features/wireframe/constants';
import { CHATTY_VERSION } from '@/core/features/wireframe/wireframe-config';

const LOADER_RETRIES = 2;
const LOADER_RETRY_TIMEOUT = 1000;

function getChattyConfig(deviceoutput: ChattyDeviceoutputType, clientDetails: null | object = null): ChattyConfigType {
    return {
        chatbot: {
            active: true,
            activeStateSticky: true,
            client: {
                chatFlowEntry: 'start_from_entrypage',
                chatFlowName: IS_PROD ? 'ind.live' : 'ind.test',
                chatFlowRelease: 'active',
                clientDetails,
                controlServerAddress: IS_PROD ? 'https://api.chat.check24.de' : 'https://api.chat.check24-test.de',
                customConsole: null,
                presentationType: 'sheet',
                sheetState: 'full',
            },
            deviceoutput,
            env: 'prod',
        },
        chattyVersion: CHATTY_VERSION,
        contact: {},
        deviceoutput,
        env: 'prod',
        name: 'indi-chatty',
        onDemand: true,
        type: 'chatty',
    };
}

export default function ChattyDataV1(attributes: ChattyDataV1Attributes) {
    const { isApp, isDesktopView } = useDeviceoutput();
    const { activities, date, destinations } = attributes;
    const loaderTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleChattySheetState = React.useCallback((event: CustomEvent<{ sheetState: ChattySheetStateType }>) => {
        if (event.detail.sheetState !== 'minimized' || !window.C24App) {
            return;
        }
        window.C24App.closeChattySheet();
    }, []);

    const activateChatty = React.useCallback(() => {
        if (!window.Check24?.loader) {
            return;
        }

        const chattyDeviceoutput = isDesktopView ? 'desktop' : isApp ? 'app' : 'mobile';

        if (chattyDeviceoutput === 'app' && window.C24App) {
            const appConfig = getChattyConfig(chattyDeviceoutput);
            window.document.addEventListener(
                WIREFRAME_DID_CHANGE_CHATTY_STATE_EVENT,
                handleChattySheetState as EventListener,
            );
            // eslint-disable-next-line fp/no-mutation
            window.Check24.loader.useChatbotApp = true;
            window.C24App.setSupportDialogHandler(() => {
                if (!window.Check24?.loader) {
                    return false;
                }
                window.Check24.loader.openChatbot(appConfig);
                return true;
            });
            return;
        }

        const config = getChattyConfig(chattyDeviceoutput, { activities, date, destinations });
        window.Check24.loader.setContactLayer(config, true);
        if (!window.Check24?.tapBar) {
            return;
        }
        window.Check24.tapBar.init(config);
    }, [date, destinations, activities, handleChattySheetState, isApp, isDesktopView]);

    const tryActivateChatty = React.useCallback(
        (retries: number) => {
            if (window.Check24?.loader) {
                activateChatty();
                return;
            }

            if (retries > 0) {
                loaderTimeoutRef.current = setTimeout(() => tryActivateChatty(retries - 1), LOADER_RETRY_TIMEOUT);
                return;
            }

            logger.error('Failed to configure Chatty: "window.Check24.loader" not available');
        },
        [activateChatty],
    );

    React.useEffect(() => {
        tryActivateChatty(LOADER_RETRIES);
        return () => {
            if (loaderTimeoutRef.current === null) {
                return;
            }
            clearTimeout(loaderTimeoutRef.current);
            loaderTimeoutRef.current = null;
            window.document.removeEventListener(
                WIREFRAME_DID_CHANGE_CHATTY_STATE_EVENT,
                handleChattySheetState as EventListener,
            );
        };
    }, [tryActivateChatty, handleChattySheetState]);

    return null;
}
